import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { trackUiError } from '../observability/clarity';
import { captureSentryException } from '../observability/sentry';

interface Props { children: ReactNode; }
interface State { hasError: boolean; }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('ErrorBoundary caught an error:', error);
    // Clarity: qualitative UI error signal
    trackUiError({
      errorType: error?.constructor?.name ?? 'Error',
      errorArea: 'react-error-boundary',
    });
    // Sentry: structured error capture
    captureSentryException(error, {
      area: 'react-boundary',
      level: 'error',
      tags: { error_type: error?.constructor?.name ?? 'Error' },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-page">
          <div className="empty-state-icon" aria-hidden="true"><AlertTriangle size={24} aria-hidden="true" /></div>
          <h2>Something went wrong</h2>
          <p>This screen ran into a problem. Your other work is unaffected — try again, or reload if it persists.</p>
          <div className="error-page-actions">
            <button className="btn btn-primary" onClick={() => this.setState({ hasError: false })}>
              Try again
            </button>
            <button className="btn btn-secondary" onClick={() => window.location.reload()}>
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
